import { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useWatch, useFormContext } from 'react-hook-form';
import moment, { Moment } from 'moment';
import { useClickAway } from 'react-use';
import { selectUserProjectTimeZone } from 'core/userContext';
import { FormField } from 'ui/formField';
import { RadioListField } from 'lib/iterable-hook-form';
import { addDurationToDate, parseDate } from 'utils/datetime';
import { DateInput, TimeInput, convertMomentToDateString } from 'ui/datePicker';
import { displayDate } from 'utils/date';
import { ApiKey } from '../types';
import {
  SpacedDatePicker,
  SpacedWarningBanner,
  LeftPaddedContainer,
  StyledSingleDateCalendar,
} from './CustomEventAllowCreationField.styled';
import {
  MAX_ALLOW_UNTIL_DAYS,
  SECURITY_FIX_ROLLOUT_DATE,
  CUSTOM_EVENT_MANAGE_ALLOW_CREATION,
  CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL,
  CUSTOM_EVENT_ALLOW_CREATION_OPTIONS,
} from './ApiKeyModal.constants';

type Props = {
  apiKey?: ApiKey;
};

export const CustomEventAllowCreationField = ({ apiKey }: Props) => {
  const projectTimeZone = useSelector(selectUserProjectTimeZone);

  const { setValue } = useFormContext();
  const [allowCreation, allowCreationUntil] = useWatch({
    name: [CUSTOM_EVENT_MANAGE_ALLOW_CREATION, CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL],
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null);

  useClickAway(datePickerRef, () => {
    setIsCalendarOpen(false);
  });

  return (
    <>
      <FormField labelText="Event creation">
        <RadioListField
          name={CUSTOM_EVENT_MANAGE_ALLOW_CREATION}
          options={CUSTOM_EVENT_ALLOW_CREATION_OPTIONS}
        />
        <SpacedDatePicker
          onClick={() => {
            if (allowCreation) setIsCalendarOpen(!isCalendarOpen);
          }}
          isDisabled={!allowCreation}
        >
          <DateInput id="allow-until-date-input" date={allowCreationUntil} />
          <TimeInput
            id="allow-until-time-input"
            time={allowCreationUntil as string}
            isReadOnly={true}
            timeZone={projectTimeZone}
          />
        </SpacedDatePicker>
        {isCalendarOpen && (
          <LeftPaddedContainer ref={datePickerRef}>
            <StyledSingleDateCalendar
              validDateRange={{
                startDate: parseDate(),
                endDate: addDurationToDate(parseDate(), {
                  days: MAX_ALLOW_UNTIL_DAYS,
                }),
              }}
              date={convertMomentToDateString(allowCreationUntil as Moment)}
              onDateChange={(val) => {
                setValue(CUSTOM_EVENT_MANAGE_ALLOW_CREATION_UNTIL, moment(val).endOf('day'));
                setIsCalendarOpen(false);
              }}
            />
          </LeftPaddedContainer>
        )}
      </FormField>

      {moment(apiKey?.createdAt || '').isBefore(SECURITY_FIX_ROLLOUT_DATE) &&
        apiKey?.customEventManageAllowCreationUntil && (
          <SpacedWarningBanner title="Security update">
            <p>
              To secure your project data, we now restrict client-side API keys from creating new
              event definitions. Because your API key was created prior to this update, new event
              definitions coming from this key are currently unrestricted.
            </p>

            <p>
              This permission will expire on&nbsp;
              {displayDate(apiKey?.customEventManageAllowCreationUntil)}. After this date, event
              track calls will be blocked from creating new event definitions unless you extend this
              permission.
            </p>
          </SpacedWarningBanner>
        )}
    </>
  );
};
